import React, { memo, useCallback } from 'react';

import { Box, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { EmailIcon, InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { SIGNER_TYPE_LABEL } from '../../borrowersDelivery.constants';
import { formatFactorDisplay } from '../../borrowersDelivery.utils';
import { BorrowersDeliveryStepTestIds } from '../../BorrowersDeliveryStep.testIds';
import { DELIVERY_METHOD_OPTIONS, DeliveryMethod } from '../../ecdEstimate.constants';
import EConsentIndicator from '../EConsentIndicator';

import {
  cardStyles,
  deliverySelectStyles,
  emailInfoStyles,
  emailLineStyles,
  fieldGroupStyles,
  fieldHelperStyles,
  fieldLabelStyles,
  identityStyles,
  mailHintStyles,
  rowStyles,
} from './SignerRow.styles';

const EMAIL_TOOLTIP =
  'Sent to the email on the borrower record. To change it, edit the borrower before preparing disclosures.';
const FACTOR_HELPER =
  'Set when the package is created. After sending, change it with Switch verify factor on the Disclosures tab.';

const AVATAR_COLORS = ['#2E7D5B', '#7C5CFC', '#B54708', '#1D6FB8', '#8A5A00'];

const getInitials = fullName => {
  const parts = String(fullName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return 'NB';
  }

  return `${parts[0][0] ?? ''}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase();
};

const getAvatarColor = signerKey => {
  const hash = String(signerKey ?? '')
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);

  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const formatShortDate = date => (date ? dayjs(date).format('MMM D') : '');
const formatLongDate = date => (date ? dayjs(date).format('MMM D, YYYY') : '');

const fieldValueStyles = { marginTop: '4px' };
const identityBodyStyles = { minWidth: 0 };
const emailStyles = { color: ({ palette }) => palette.text.primary, wordBreak: 'break-all' };
const factorValueStyles = { fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.02em' };

const FieldCell = ({ label, children }) => (
  <Box>
    <Typography sx={fieldLabelStyles} variant="regular12">
      {label}
    </Typography>
    <Box sx={fieldValueStyles}>{children}</Box>
  </Box>
);

FieldCell.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const SignerRow = ({ signer, ecdEstimate, onDeliveryChange }) => {
  const isMail = signer.deliveryMethod === DeliveryMethod.MAIL;

  const handleDeliveryChange = useCallback(
    event => onDeliveryChange(signer.signerKey, event.target.value),
    [onDeliveryChange, signer.signerKey],
  );

  return (
    <Box data-testid={BorrowersDeliveryStepTestIds.SIGNER_ROW} sx={cardStyles}>
      <Box sx={rowStyles}>
        <Box sx={identityStyles.container}>
          <Box sx={identityStyles.avatar(getAvatarColor(signer.signerKey))}>
            {getInitials(signer.fullName)}
          </Box>
          <Box sx={identityBodyStyles}>
            <Typography variant="bold14">{signer.fullName}</Typography>
            <Typography sx={identityStyles.role} variant="regular12">
              {SIGNER_TYPE_LABEL[signer.signerType]}
            </Typography>
            <Box sx={emailLineStyles}>
              <Typography
                data-testid={BorrowersDeliveryStepTestIds.SIGNER_EMAIL}
                sx={emailStyles}
                variant="regular12"
              >
                {signer.email || 'No email on file'}
              </Typography>
              <Tooltip arrow title={EMAIL_TOOLTIP}>
                <Box component="span" sx={emailInfoStyles}>
                  <InfoIcon size={13} />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={fieldGroupStyles}>
          <FieldCell label="Verify (2nd factor)">
            <Typography
              data-testid={BorrowersDeliveryStepTestIds.SIGNER_FACTOR}
              sx={factorValueStyles}
              variant="regular12"
            >
              {formatFactorDisplay(signer)}
            </Typography>
            <Typography sx={fieldHelperStyles} variant="regular12">
              {FACTOR_HELPER}
            </Typography>
          </FieldCell>
          <FieldCell label="E-consent">
            <EConsentIndicator status={signer.eConsentStatus} />
          </FieldCell>
          <FieldCell label="Delivery">
            <Box data-testid={BorrowersDeliveryStepTestIds.SIGNER_DELIVERY}>
              <Select
                data-testid={BorrowersDeliveryStepTestIds.SIGNER_DELIVERY_SELECT}
                size="small"
                sx={deliverySelectStyles}
                value={signer.deliveryMethod}
                onChange={handleDeliveryChange}
              >
                {DELIVERY_METHOD_OPTIONS.map(({ value, label }) => (
                  <MenuItem
                    data-testid={BorrowersDeliveryStepTestIds.getSignerDeliveryOptionId(value)}
                    key={value}
                    value={value}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </FieldCell>
        </Box>
      </Box>

      {isMail && (
        <Box data-testid={BorrowersDeliveryStepTestIds.SIGNER_MAIL_HINT} sx={mailHintStyles}>
          <EmailIcon size={16} />
          <Typography variant="regular12">
            <strong>Mail</strong>
            {ecdEstimate.mailReceiptDate
              ? ` — receipt presumed ${formatShortDate(
                  ecdEstimate.mailReceiptDate,
                )} (3 business days).`
              : ' — receipt presumed 3 business days after send.'}
            {ecdEstimate.hasEstimate
              ? ` Earliest closing moves to ${formatLongDate(
                  ecdEstimate.estimatedDate,
                )} (estimated).`
              : ' Earliest closing moves out accordingly.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

SignerRow.propTypes = {
  signer: PropTypes.shape({
    signerKey: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    signerType: PropTypes.string,
    email: PropTypes.string,
    factorType: PropTypes.string,
    ssnLast4: PropTypes.string,
    zipCode: PropTypes.string,
    eConsentStatus: PropTypes.string,
    deliveryMethod: PropTypes.string,
  }).isRequired,
  ecdEstimate: PropTypes.shape({
    hasEstimate: PropTypes.bool,
    estimatedDate: PropTypes.instanceOf(Date),
    mailReceiptDate: PropTypes.instanceOf(Date),
  }).isRequired,
  onDeliveryChange: PropTypes.func.isRequired,
};

export default memo(SignerRow);
